// src/components/dashboards/Vendor/adminverification/ProfessionalVerificationAdmin.jsx

import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  ShieldCheck,
  FileText,
  ExternalLink,
  CheckCircle2,
  XCircle,
  UserRound
} from "lucide-react";

import "./professionalVerification.css";

export default function ProfessionalVerificationAdmin() {

  const [verifications, setVerifications] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const token =
    localStorage.getItem("token");


  /* =====================================================
     API
  ===================================================== */

  const api = axios.create({
    baseURL: "/api/admin",
    headers: {
      Authorization: `Bearer ${token}`
    }
  });


  /* =====================================================
     LOAD VERIFICATIONS
  ===================================================== */

  const loadVerifications = async () => {

    try {

      setLoading(true);

      const res =
        await api.get(
          "/professional-verifications"
        );

      setVerifications(
        res.data || []
      );

    } catch (err) {

      console.error(
        "Failed to load professional verifications:",
        err
      );

      toast.error(
        "Unable to load verification requests"
      );

    } finally {

      setLoading(false);

    }
  };


  useEffect(() => {
    loadVerifications();
  }, []);


  /* =====================================================
     APPROVE
  ===================================================== */

  const approve = async (userId) => {

    try {

      await api.post(
        `/professional-verifications/${userId}/approve`
      );

      toast.success(
        "Verification approved"
      );

      loadVerifications();

    } catch (err) {

      console.error(
        "Approval failed:",
        err
      );

      toast.error(
        "Unable to approve verification"
      );
    }
  };


  /* =====================================================
     REJECT
  ===================================================== */

  const reject = async (userId) => {

    const reason =
      prompt(
        "Enter rejection reason"
      );

    if (!reason?.trim()) {
      return;
    }

    try {

      await api.post(
        `/professional-verifications/${userId}/reject`,
        {
          reason: reason.trim()
        }
      );

      toast.success(
        "Verification rejected"
      );

      loadVerifications();

    } catch (err) {

      console.error(
        "Rejection failed:",
        err
      );

      toast.error(
        "Unable to reject verification"
      );
    }
  };


  /* =====================================================
     STATUS
  ===================================================== */

  const getStatusClass = (status) => {

    switch (status) {

      case "approved":
        return "verification-status approved";

      case "rejected":
        return "verification-status rejected";

      case "pending":
        return "verification-status pending";

      default:
        return "verification-status";
    }
  };


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {

    return (
      <section className="admin-card">

        <div className="verification-admin-loading">

          <ShieldCheck size={22} />

          <span>
            Loading verification requests...
          </span>

        </div>

      </section>
    );
  }


  /* =====================================================
     UI
  ===================================================== */

  return (
    <section className="admin-card">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="verification-admin-header">

        <div>

          <div className="verification-admin-heading">

            <ShieldCheck size={20} />

            <h3>
              Professional Verification
            </h3>

          </div>

          <p>
            Review professional qualification
            documents submitted by Engineers
            and Architects.
          </p>

        </div>

        <div className="verification-count">

          {verifications.length}

          <span>
            Requests
          </span>

        </div>

      </div>


      {/* =================================================
          EMPTY
      ================================================= */}

      {verifications.length === 0 ? (

        <div className="verification-empty">

          <div className="verification-empty-icon">
            <CheckCircle2 size={24} />
          </div>

          <strong>
            No verification requests
          </strong>

          <span>
            New professional applications will
            appear here.
          </span>

        </div>

      ) : (

        <div className="verification-table-wrapper">

          <table className="admin-table">

            <thead>

              <tr>

                <th>
                  Applicant
                </th>

                <th>
                  Profession
                </th>

                <th>
                  Status
                </th>

                <th>
                  Qualification
                </th>

                <th>
                  Action
                </th>

              </tr>

            </thead>


            <tbody>

              {verifications.map((u) => {

                const verification =
                  u.professionalVerification;

                const documents =
                  verification?.documents || {};

                const certificateUrl =
                  documents.degreeUrl ||
                  documents.certificateUrl;


                return (

                  <tr key={u._id}>

                    {/* =================================
                        APPLICANT
                    ================================= */}

                    <td>

                      <div className="verification-applicant">

                        <div className="verification-user-icon">

                          <UserRound
                            size={17}
                          />

                        </div>

                        <div>

                          <strong>
                            {u.name}
                          </strong>

                          <span>
                            {u.email}
                          </span>

                        </div>

                      </div>

                    </td>


                    {/* =================================
                        PROFESSION
                    ================================= */}

                    <td>

                      <span className="profession-label">
                        {u.profession}
                      </span>

                    </td>


                    {/* =================================
                        STATUS
                    ================================= */}

                    <td>

                      <span
                        className={
                          getStatusClass(
                            verification?.status
                          )
                        }
                      >

                        {verification?.status ===
                          "pending" && (
                          <ShieldCheck size={13} />
                        )}

                        {verification?.status ===
                          "approved" && (
                          <CheckCircle2 size={13} />
                        )}

                        {verification?.status ===
                          "rejected" && (
                          <XCircle size={13} />
                        )}

                        {verification?.status ||
                          "Unknown"}

                      </span>

                    </td>


                    {/* =================================
                        CERTIFICATE
                    ================================= */}

                    <td>

                      {certificateUrl ? (

                        <a
                          href={certificateUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="certificate-view-btn"
                        >

                          <FileText
                            size={15}
                          />

                          <span>
                            View Certificate
                          </span>

                          <ExternalLink
                            size={13}
                          />

                        </a>

                      ) : (

                        <span className="certificate-missing">
                          Certificate not available
                        </span>

                      )}

                    </td>


                    {/* =================================
                        ACTION
                    ================================= */}

                    <td>

                      {verification?.status ===
                        "pending" ? (

                        <div className="verification-actions">

                          <button
                            type="button"
                            className="admin-primary-btn"
                            onClick={() =>
                              approve(u._id)
                            }
                          >

                            <CheckCircle2
                              size={14}
                            />

                            Approve

                          </button>


                          <button
                            type="button"
                            className="admin-secondary-btn"
                            onClick={() =>
                              reject(u._id)
                            }
                          >

                            <XCircle
                              size={14}
                            />

                            Reject

                          </button>

                        </div>

                      ) : (

                        <span className="verification-completed">
                          Reviewed
                        </span>

                      )}

                    </td>

                  </tr>

                );

              })}

            </tbody>

          </table>

        </div>

      )}

    </section>
  );
}